import { jsPDF } from 'jspdf';
import 'jspdf/dist/polyfills.es.js';
import { totalTravellers } from './groupSize';

const YOUR_TOUR_BLUE = [0, 82, 155];   // brand primary
const GRAY_DARK      = [31, 41, 55];
const GRAY_MID       = [107, 114, 128];
const GRAY_LIGHT     = [229, 231, 235];
const WHITE          = [255, 255, 255];

function fmtAmount(n, sym = '£') {
  if (n == null || n === '') return '—';
  return `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Generates and downloads a "Your Tour" finance summary PDF for a given request.
 *
 * @param {object} request            - The full request Firestore document
 * @param {Array}  invoices           - Array of regular (non-match-ticket) invoice documents
 * @param {object|null} matchTicketInvoice - Match ticket invoice document, if generated
 */
export function downloadFinanceSummaryPdf(request, invoices, matchTicketInvoice = null) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PAGE_W = 210;
  const MARGIN = 16;
  const COL_W  = PAGE_W - MARGIN * 2;
  let y = 0;

  const sym = request.quoteDetails
    ? (request.currency === 'EUR' ? '€' : request.currency === 'USD' ? '$' : '£')
    : '£';

  // ── Header band ──────────────────────────────────────────────────────────
  doc.setFillColor(...YOUR_TOUR_BLUE);
  doc.rect(0, 0, PAGE_W, 28, 'F');

  doc.setTextColor(...WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Your Tour', MARGIN, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('Finance Summary — Confidential', MARGIN, 20);

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(`Generated: ${today}`, PAGE_W - MARGIN, 20, { align: 'right' });

  y = 36;

  // ── Tour details block ───────────────────────────────────────────────────
  doc.setTextColor(...GRAY_DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Tour Details', MARGIN, y);
  y += 5;

  doc.setDrawColor(...GRAY_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 5;

  const detailFields = [
    ['Sport',        request.sport],
    ['Destination',  request.destination],
    ['Departure',    fmtDate(request.departureDate)],
    ['Return',       fmtDate(request.returnDate)],
    ['Group Type',   request.groupType],
    ['Travellers',   totalTravellers(request) ? `${totalTravellers(request)} pax` : '—'],
    ['Contact',      request.contactName || '—'],
  ];

  const colMid = MARGIN + COL_W / 2;
  detailFields.forEach(([label, value], i) => {
    const col = i % 2 === 0 ? MARGIN : colMid;
    if (i % 2 === 0 && i > 0) y += 7;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_MID);
    doc.text(label.toUpperCase(), col, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_DARK);
    doc.text(String(value || '—'), col, y + 4);
  });

  y += 12;

  // ── Quote summary (inclusions only — no individual prices) ──────────────
  if (request.quoteDetails) {
    const qd = request.quoteDetails;
    const INTERNAL_TYPES = new Set(['mst_staff_cost', 'davids_cost', 'referral_fee']);
    const visibleItems = (qd.breakdown || []).filter((row) => !INTERNAL_TYPES.has(row.pricingType));

    if (visibleItems.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...GRAY_DARK);
      doc.text('What\'s Included', MARGIN, y);
      y += 5;
      doc.setDrawColor(...GRAY_LIGHT);
      doc.line(MARGIN, y, PAGE_W - MARGIN, y);
      y += 6;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...GRAY_DARK);

      visibleItems.forEach((row) => {
        doc.text(`• ${row.categoryName}`, MARGIN + 3, y);
        y += 5.5;
        if (y > 260) { doc.addPage(); y = 20; }
      });

      y += 6;
    }
  }

  // ── Instalment plan ──────────────────────────────────────────────────────
  if (invoices.length > 0) {
    if (y > 220) { doc.addPage(); y = 20; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...GRAY_DARK);
    doc.text('Instalment Plan', MARGIN, y);
    y += 5;
    doc.setDrawColor(...GRAY_LIGHT);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 5;

    // Table header
    doc.setFillColor(248, 249, 250);
    doc.rect(MARGIN, y - 1, COL_W, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_MID);
    const col1 = MARGIN + 2;
    const col2 = MARGIN + 55;
    const col3 = MARGIN + 100;
    const col4 = PAGE_W - MARGIN - 2;
    doc.text('Invoice No.', col1, y + 4);
    doc.text('Description', col2, y + 4);
    doc.text('Due Date', col3, y + 4);
    doc.text('Amount', col4, y + 4, { align: 'right' });
    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    const sorted = [...invoices].sort((a, b) => (a.instalmentNumber ?? 0) - (b.instalmentNumber ?? 0));
    sorted.forEach((inv, i) => {
      if (i % 2 === 0) {
        doc.setFillColor(252, 252, 253);
        doc.rect(MARGIN, y - 3.5, COL_W, 6.5, 'F');
      }
      const invSym = inv.currency === 'EUR' ? '€' : inv.currency === 'USD' ? '$' : '£';
      doc.setTextColor(...GRAY_DARK);
      doc.text(inv.invoiceNumber || '—', col1, y);
      doc.text(inv.instalmentLabel || '—', col2, y);
      doc.text(fmtDate(inv.dueDate), col3, y);
      doc.text(fmtAmount(inv.amount, invSym), col4, y, { align: 'right' });
      y += 6.5;
      if (y > 265) { doc.addPage(); y = 20; }
    });

    // Invoice total
    const invoiceTotal = sorted.reduce((s, inv) => s + (Number(inv.amount) || 0), 0);
    y += 2;
    doc.setDrawColor(...GRAY_LIGHT);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_DARK);
    doc.text('Total', col1, y);
    doc.text(fmtAmount(invoiceTotal, sym), col4, y, { align: 'right' });
    y += 10;
  }

  // ── Match Tickets (Additional) ───────────────────────────────────────────
  if (matchTicketInvoice) {
    if (y > 230) { doc.addPage(); y = 20; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...GRAY_DARK);
    doc.text('Match Tickets (Additional)', MARGIN, y);
    y += 5;
    doc.setDrawColor(...GRAY_LIGHT);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 5;

    const mtd = matchTicketInvoice.matchTicketDetails || {};
    const mtSym = matchTicketInvoice.currency === 'EUR' ? '€' : matchTicketInvoice.currency === 'USD' ? '$' : '£';

    // Fixture detail rows
    const mtRows = [
      mtd.homeTeam && ['Fixture', `${mtd.homeTeam} (La Liga)`],
      mtd.fixtureDate && ['Date', fmtDate(mtd.fixtureDate)],
      mtd.kickOffTime && ['Kick-off', mtd.kickOffTime],
      mtd.numberOfTickets && ['Tickets', String(mtd.numberOfTickets)],
      mtd.chargeToCustomerPerTicket && ['Price per ticket', fmtAmount(mtd.chargeToCustomerPerTicket, mtSym)],
    ].filter(Boolean);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_DARK);
    mtRows.forEach(([label, value]) => {
      doc.setTextColor(...GRAY_MID);
      doc.text(label, MARGIN + 2, y);
      doc.setTextColor(...GRAY_DARK);
      doc.text(value, MARGIN + 55, y);
      y += 5.5;
    });

    y += 4;

    // Invoice line
    const col1m = MARGIN + 2;
    const col4m = PAGE_W - MARGIN - 2;
    doc.setFillColor(252, 252, 253);
    doc.rect(MARGIN, y - 3.5, COL_W, 6.5, 'F');
    doc.setTextColor(...GRAY_DARK);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(matchTicketInvoice.invoiceNumber || '—', col1m, y);
    doc.text('La Liga Match Tickets', MARGIN + 55, y);
    doc.text(fmtDate(matchTicketInvoice.dueDate), MARGIN + 100, y);
    doc.setFont('helvetica', 'bold');
    doc.text(fmtAmount(matchTicketInvoice.amount, mtSym), col4m, y, { align: 'right' });
    y += 8;

    doc.setDrawColor(...GRAY_LIGHT);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_DARK);
    doc.text('Total', col1m, y);
    doc.text(fmtAmount(matchTicketInvoice.amount, mtSym), col4m, y, { align: 'right' });
    y += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...GRAY_MID);
    doc.text('This invoice is separate from the main instalment plan and is payable within 7 days.', MARGIN, y);
    y += 10;
  }

  // ── Footer ───────────────────────────────────────────────────────────────
  const FOOTER_Y = 285;
  doc.setDrawColor(...GRAY_LIGHT);
  doc.line(MARGIN, FOOTER_Y - 2, PAGE_W - MARGIN, FOOTER_Y - 2);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY_MID);
  doc.text('This document is for finance purposes only and has been prepared by MST Sports Tours on behalf of Your Tour.', MARGIN, FOOTER_Y + 2);
  doc.text('Please do not distribute externally.', MARGIN, FOOTER_Y + 6);

  // ── Save ──────────────────────────────────────────────────────────────────
  const filename = `YourTour-Finance-${request.sport?.replace(/\s+/g, '-')}-${request.destination?.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
